import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { subscriptionService } from '../../services/subscriptionService';
import { agentService } from '../../services/agentService';
import NavigationSidebar from '../../components/NavigationSidebar';
import TutorialAccessButton from '../../components/TutorialAccessButton';
import ActivityFeed from '../../components/ActivityFeed';
import AgentCard from './components/AgentCard';
import QuickStatsCard from './components/QuickStatsCard';
import WelcomeBanner from './components/WelcomeBanner';
import SubscriptionBanner from './components/SubscriptionBanner';
import PaymentFailureBanner from './components/PaymentFailureBanner';



const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tutorialProgress] = useState(0);
  const [subscription, setSubscription] = useState(null);
  const [agentLimitStatus, setAgentLimitStatus] = useState(null);
  const [paymentFailures, setPaymentFailures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSpendingInsights, setShowSpendingInsights] = useState(false);
  const [dashboardMetrics, setDashboardMetrics] = useState({
    tasksCompleted: 0,
    activeTasks: 0,
    avgEfficiency: 0
  });
  const [agentQuantities, setAgentQuantities] = useState({
    manager: 0,
    marketing: 0,
    productivity: 0,
    feedback: 0,
    onboarding: 0
  });

  useEffect(() => {
    if (user?.id) {
      loadAllData();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadSubscription(),
        loadAgentLimitStatus(),
        loadPaymentFailures(),
        loadDashboardMetrics()
      ]);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSubscription = async () => {
    try {
      const { data, error } = await subscriptionService?.getUserSubscription(user?.id);
      if (!error) {
        setSubscription(data);
      }
    } catch (error) {
      console.error('Error loading subscription:', error);
    }
  };

  const loadAgentLimitStatus = async () => {
    try {
      const { data, error } = await subscriptionService?.getAgentLimitStatus(user?.id);
      if (!error) {
        setAgentLimitStatus(data);
      }
    } catch (error) {
      console.error('Error loading agent limit status:', error);
    }
  };

  const loadPaymentFailures = async () => {
    try {
      const { data, error } = await subscriptionService?.getPaymentFailures(user?.id);
      if (!error) {
        setPaymentFailures(data || []);
      }
    } catch (error) {
      console.error('Error loading payment failures:', error);
    }
  };

  const loadDashboardMetrics = async () => {
    try {
      const { data, error } = await agentService?.getDashboardMetrics(user?.id);
      if (!error && data) {
        setDashboardMetrics({
          tasksCompleted: data?.tasks_completed || 0,
          activeTasks: data?.active_tasks || 0,
          avgEfficiency: data?.avg_efficiency || 0
        });
      }
    } catch (error) {
      console.error('Error loading dashboard metrics:', error);
    }
  };

  const getMaxAgents = () => {
    // All 5 agent types are available for all tiers
    // Total agent limits are enforced by getMaxTotalAgents() and quantity controls
    return 5;
  };

  const getRequiredPlan = (agentIndex) => {
    if (agentIndex < 2) return null;
    if (agentIndex < 4) return 'DraftIQ+';
    return 'Enterprise';
  };

  const agentsData = [
    {
      id: 'manager',
      name: 'Manager Agent',
      description: 'Central communication hub coordinating all agent activities and task distribution across your business operations.',
      icon: 'Users',
      color: 'purple',
      status: 'active',
      tasksCompleted: 247,
      activeTasks: 12,
      efficiency: 94,
      recentActivity: 'Coordinated task distribution to Marketing Operations and assigned 3 new priority items',
      route: '/manager-agent-interface'
    },
    {
      id: 'marketing',
      name: 'Marketing Operations',
      description: 'Creates email campaigns, searches high-performing ads, and generates content videos and templates for your industry.',
      icon: 'TrendingUp',
      color: 'blue',
      status: 'busy',
      tasksCompleted: 189,
      activeTasks: 8,
      efficiency: 87,
      recentActivity: 'Generated 5 email campaign templates and analyzed 12 competitor ads in your industry',
      route: '/marketing-agent-interface'
    },
    {
      id: 'productivity',
      name: 'Personal Productivity',
      description: 'Keeps you organized with daily duty checklists and efficient task management for maximum productivity.',
      icon: 'CheckSquare',
      color: 'emerald',
      status: 'active',
      tasksCompleted: 312,
      activeTasks: 15,
      efficiency: 91,
      recentActivity: 'Created 8 task checklists and sent 3 priority reminders for today',
      route: '/personal-productivity-agent-interface'
    },
    {
      id: 'feedback',
      name: 'Feedback & Analytics',
      description: 'Analyzes customer feedback, tracks competitor strategies, and provides actionable insights for business growth.',
      icon: 'BarChart3',
      color: 'orange',
      status: 'active',
      tasksCompleted: 156,
      activeTasks: 6,
      efficiency: 89,
      recentActivity: 'Analyzed 24 customer reviews and identified 3 trending competitor strategies',
      route: '/feedback-analytics-agent-interface'
    },
    {
      id: 'onboarding',
      name: 'Onboarding Agent',
      description: 'Guides new users through setup and configuration, personalizing the experience based on business needs.',
      icon: 'Sparkles',
      color: 'rose',
      status: 'active',
      tasksCompleted: 89,
      activeTasks: 3,
      efficiency: 95,
      recentActivity: 'Completed 2 new user onboarding sessions and configured agent preferences',
      route: '/onboarding-agent-interface'
    },
  ];

  const maxAgents = getMaxAgents();
  const availableAgents = agentsData?.slice(0, maxAgents);
  const lockedAgents = agentsData?.slice(maxAgents);

  const quickStats = [
    {
      title: 'Active Agents',
      value: availableAgents?.length,
      icon: 'Users',
      iconColor: 'var(--color-blue-500)'
    },
    {
      title: 'Tasks Completed',
      value: dashboardMetrics?.tasksCompleted,
      icon: 'CheckCircle',
      iconColor: 'var(--color-green-500)'
    },
    {
      title: 'Active Tasks',
      value: dashboardMetrics?.activeTasks,
      icon: 'Clock',
      iconColor: 'var(--color-orange-500)'
    },
    {
      title: 'Avg Efficiency',
      value: `${Math.round(dashboardMetrics?.avgEfficiency)}%`,
      icon: 'TrendingUp',
      iconColor: 'var(--color-purple-500)'
    }
  ];

  const recentActivities = availableAgents?.map(agent => ({
    id: agent?.id,
    agentName: agent?.name,
    action: agent?.recentActivity,
    timestamp: 'Just now',
    icon: agent?.icon,
    color: agent?.color
  }));

  const handleQuantityChange = (agentId, newQuantity) => {
    // Calculate total agents across all types
    const currentTotal = Object.values(agentQuantities)?.reduce((sum, qty) => sum + qty, 0);
    const quantityDiff = newQuantity - (agentQuantities?.[agentId] || 0);
    const newTotal = currentTotal + quantityDiff;

    // Get max total agents based on subscription tier
    const tier = subscription?.subscription_plans?.tier || 'free';
    let maxTotal = 2; // free
    if (tier === 'enterprise') maxTotal = 25;
    else if (tier === 'draftiq_plus') maxTotal = 4;

    // Check if new total exceeds limit
    if (newTotal > maxTotal) {
      const tierName = tier === 'free' ? 'Free' : tier === 'draftiq_plus' ? 'DraftIQ+' : 'Enterprise';
      alert(`Your ${tierName} plan allows up to ${maxTotal} total agents. Please upgrade to add more agents.`);
      return;
    }

    setAgentQuantities(prev => ({
      ...prev,
      [agentId]: newQuantity
    }));
  };

  const getTotalAgents = () => {
    return Object.values(agentQuantities)?.reduce((sum, qty) => sum + qty, 0);
  };

  const getMaxTotalAgents = () => {
    const tier = subscription?.subscription_plans?.tier || 'free';
    if (tier === 'enterprise') return 25;
    if (tier === 'draftiq_plus') return 4;
    return 2; // free
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-background">
        <NavigationSidebar />
        <div className="flex-1 flex items-center justify-center ml-0 lg:ml-60">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <NavigationSidebar />
      <div className="lg:ml-64 min-h-screen">
        <div className="relative overflow-hidden">
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 pointer-events-none" />
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Payment Failure Banner */}
            {paymentFailures?.length > 0 && (
              <div className="mb-6">
                <PaymentFailureBanner 
                  failures={paymentFailures} 
                  onUpdatePayment={() => navigate('/billing')} 
                />
              </div>
            )}

            {/* Subscription Banner */}
            {subscription && (
              <div className="mb-6">
                <SubscriptionBanner 
                  subscription={subscription} 
                  onUpgrade={() => navigate('/pricing')}
                  onManageBilling={() => navigate('/billing')}
                />
              </div>
            )}

            {/* Welcome Banner with Glass Effect */}
            <div className="mb-8">
              <div className="glass-strong rounded-3xl p-8 shadow-glass-lg backdrop-blur-xl">
                <WelcomeBanner 
                  user={user} 
                  tutorialProgress={tutorialProgress} 
                  onStartOnboarding={() => navigate('/onboarding')}
                  onUpgrade={() => navigate('/pricing')}
                />
              </div>
            </div>

            {/* Quick Stats with Elevated Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              <div className="elevated-card rounded-2xl p-6 backdrop-blur-sm hover-lift">
                <QuickStatsCard
                  title="Active Agents"
                  value={agentLimitStatus?.active_agents || 0}
                  max={getMaxAgents()}
                  icon="Users"
                  color="primary"
                  description="Number of active AI agents"
                  change={0}
                />
              </div>
              <div className="elevated-card rounded-2xl p-6 backdrop-blur-sm hover-lift">
                <QuickStatsCard
                  title="Tasks Completed"
                  value={dashboardMetrics?.tasksCompleted}
                  icon="CheckCircle"
                  color="success"
                  description="Total tasks completed"
                  change={0}
                />
              </div>
              <div className="elevated-card rounded-2xl p-6 backdrop-blur-sm hover-lift">
                <QuickStatsCard
                  title="Active Tasks"
                  value={dashboardMetrics?.activeTasks}
                  icon="Clock"
                  color="warning"
                  description="Currently active tasks"
                  change={0}
                />
              </div>
              <div className="elevated-card rounded-2xl p-6 backdrop-blur-sm hover-lift">
                <QuickStatsCard
                  title="Efficiency"
                  value={`${Math.round(dashboardMetrics?.avgEfficiency)}%`}
                  icon="TrendingUp"
                  color="accent"
                  description="Average efficiency rate"
                  change={0}
                />
              </div>
            </div>

            {/* Agents Section */}
            <div className="mb-12">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-foreground font-heading">
                    Your AI Agents
                  </h2>
                  <p className="text-muted-foreground mt-1">
                    {agentLimitStatus?.active_agents || 0} of {getMaxAgents()} agents active
                  </p>
                </div>
                <TutorialAccessButton onTutorialStart={() => navigate('/tutorial')} />
              </div>

              {loading ? (
                <div className="flex justify-center items-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {agentsData?.map((agent, index) => {
                    const isLocked = index >= getMaxAgents();
                    const requiredPlan = getRequiredPlan(index);

                    return (
                      <AgentCard
                        key={agent?.id}
                        agent={agent}
                        locked={isLocked}
                        requiredPlan={requiredPlan}
                        onUpgrade={() => navigate('/pricing')}
                        subscription={subscription}
                        agentQuantity={agentQuantities?.[agent?.id] || 0}
                        onQuantityChange={handleQuantityChange}
                        totalAgents={getTotalAgents()}
                        maxTotalAgents={getMaxTotalAgents()}
                      />
                    );
                  })}
                </div>
              )}
            </div>

            {/* Activity Feed with Glass Effect */}
            <div className="glass-strong rounded-3xl p-8 shadow-glass backdrop-blur-sm">
              <h2 className="text-2xl font-bold text-foreground mb-6 font-heading">
                Recent Activity
              </h2>
              <ActivityFeed limit={5} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard;